import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/client"
import { formatDateLabel, formatMoney } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"

export function InvoicePrintPage() {
  const { orderId = "" } = useParams()
  const query = useQuery({
    queryFn: () => api.invoice(orderId),
    queryKey: queryKeys.invoice(orderId),
  })
  const invoice = query.data?.invoice

  if (query.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (!invoice) return <p className="text-sm text-destructive">Invoice not found.</p>

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6 print-root">
      <div className="no-print flex items-center justify-between">
        <Button asChild size="sm" variant="ghost">
          <Link to={`/orders/${orderId}`}>Back to order</Link>
        </Button>
        <Button onClick={() => window.print()} size="sm" type="button">
          Print
        </Button>
      </div>
      <header className="border-b border-border pb-4">
        <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Haute Organic</p>
        <h1 className="mt-2 text-2xl font-semibold">Invoice {invoice.orderName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Week of {formatDateLabel(invoice.weekStartDate)} – {formatDateLabel(invoice.weekEndDate)}
        </p>
      </header>
      <section className="text-sm">
        <p className="font-medium">{invoice.customer.name}</p>
        <p className="text-muted-foreground">{invoice.customer.email}</p>
      </section>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-2 font-medium">Item</th>
            <th className="py-2 font-medium">Qty</th>
            <th className="py-2 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr className="border-b border-border" key={line.id}>
              <td className="py-2">
                {line.name}
                {line.deliveryDate ? (
                  <span className="block text-xs text-muted-foreground">
                    {formatDateLabel(line.deliveryDate)}
                    {line.mealTime ? ` · ${line.mealTime}` : ""}
                  </span>
                ) : null}
              </td>
              <td className="py-2">{line.quantity}</td>
              <td className="py-2 text-right">{formatMoney(line.priceSubtotal, invoice.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="pt-3 font-semibold" colSpan={2}>
              Total
            </td>
            <td className="pt-3 text-right font-semibold">
              {formatMoney(invoice.amountTotal, invoice.currency)}
            </td>
          </tr>
        </tfoot>
      </table>
      <p className="text-xs text-muted-foreground">{invoice.disclaimer}</p>
    </div>
  )
}
